import React, { useState, useEffect, useCallback, useRef } from "react";
import { App as CapacitorApp } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import { http } from "./lib/api";
import { usePaymentReturn } from "./hooks/usePaymentReturn";
import { resolveDeviceIdentity, clearLegacyDeviceIdentity } from "./lib/device";
import { addPendingPayment, clearPendingPayments, parseCheckoutReturnUrl } from "./lib/checkout";
import Onboarding from "./components/Onboarding";
import ChatView from "./components/ChatView";
import Shop from "./components/Shop";
import SharedExperiment from "./components/SharedExperiment";

export default function App() {
  const [identity, setIdentity] = useState({ deviceId: null, legacyDeviceId: null, ready: false });
  const [credits, setCredits] = useState(null);
  const [sessionId, setSessionId] = useState(localStorage.getItem("pai_session"));
  const [onboarded, setOnboarded] = useState(!!localStorage.getItem("pai_onboarded"));
  const [shop, setShop] = useState({ open: false, hint: "" });
  const [toast, setToast] = useState(null);
  const handledReturnUrlRef = useRef("");

  const showToast = useCallback((msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4500);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const boot = async () => {
      try {
        const nextIdentity = await resolveDeviceIdentity();
        if (cancelled) return;
        setIdentity({ ...nextIdentity, ready: true });
      } catch (error) {
        console.warn("Geratekennung konnte nicht geladen werden:", error);
        const fallbackId = (crypto.randomUUID && crypto.randomUUID()) || `dev-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        localStorage.setItem("pai_device", fallbackId);
        if (!cancelled) {
          setIdentity({ deviceId: fallbackId, legacyDeviceId: null, ready: true });
          showToast("Geratekennung wurde neu angelegt.", "error");
        }
      }
    };
    boot();
    return () => {
      cancelled = true;
    };
  }, [showToast]);

  useEffect(() => {
    if (!identity.ready || !identity.deviceId) return;
    let cancelled = false;
    const init = async () => {
      try {
        const payload = { device_id: identity.deviceId };
        if (identity.legacyDeviceId) {
          payload.legacy_device_id = identity.legacyDeviceId;
        }
        const { data } = await http.post("/device/register", payload);
        if (cancelled) return;
        setCredits({ standard: data.standard_credits, pro: data.pro_credits });
        if (identity.legacyDeviceId) {
          clearLegacyDeviceIdentity();
        }
      } catch (e) {
        console.warn("Initialisierung fehlgeschlagen:", e);
        if (!cancelled) {
          showToast("Verbindung zum Server fehlgeschlagen.", "error");
        }
      }
    };
    init();
    return () => {
      cancelled = true;
    };
  }, [identity.ready, identity.deviceId, identity.legacyDeviceId, showToast]);

  usePaymentReturn(identity.deviceId, setCredits, showToast);

  useEffect(() => {
    let handle;
    const closeCheckoutBrowser = () => {
      Browser.close().catch(() => {});
    };
    const onCheckoutReturn = (url) => {
      if (!url || handledReturnUrlRef.current === url) return;
      const checkoutReturn = parseCheckoutReturnUrl(url);
      if (!checkoutReturn) return;
      handledReturnUrlRef.current = url;
      closeCheckoutBrowser();

      if (checkoutReturn.status === "cancelled") {
        clearPendingPayments();
        setShop({ open: false, hint: "" });
        showToast("Zahlung abgebrochen. Der Shop ist wieder frei.", "error");
        return;
      }

      if (checkoutReturn.sessionId) {
        addPendingPayment(checkoutReturn.sessionId);
      }
      setShop({ open: false, hint: "" });
    };

    CapacitorApp.getLaunchUrl().then(({ url }) => {
      onCheckoutReturn(url);
    });

    CapacitorApp.addListener("appUrlOpen", ({ url }) => onCheckoutReturn(url)).then((subscription) => {
      handle = subscription;
    });

    return () => {
      if (handle) handle.remove();
    };
  }, [showToast]);

  useEffect(() => {
    const onCredits = (event) => setCredits(event.detail);
    window.addEventListener("physikai:credits", onCredits);
    return () => window.removeEventListener("physikai:credits", onCredits);
  }, []);

  const newChat = () => {
    localStorage.removeItem("pai_session");
    setSessionId(null);
  };

  const switchSession = (id) => {
    localStorage.setItem("pai_session", id);
    setSessionId(id);
  };

  const shareMatch = window.location.pathname.match(/^\/experiment\/([A-Za-z0-9-]+)/);
  if (shareMatch) {
    return <SharedExperiment token={shareMatch[1]} />;
  }

  if (!identity.ready) {
    return (
      <div className="min-h-[100dvh] bg-bg text-white font-body flex items-center justify-center px-6">
        <div className="text-center max-w-sm space-y-3">
          <div className="text-sm uppercase tracking-[0.18em] text-muted">Physik AI</div>
          <h1 className="font-heading text-2xl font-bold">App wird gestartet</h1>
          <p className="text-sm text-muted">Geratekennung und Kontostand werden geladen.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-bg text-white font-body">
      {!onboarded ? (
        <Onboarding
          onStart={() => {
            localStorage.setItem("pai_onboarded", "1");
            setOnboarded(true);
          }}
        />
      ) : (
        <ChatView
          deviceId={identity.deviceId}
          sessionId={sessionId}
          credits={credits}
          setCredits={setCredits}
          onOpenShop={(hint) => setShop({ open: true, hint: hint || "" })}
          onNewChat={newChat}
          onSwitchSession={switchSession}
          showToast={showToast}
        />
      )}
      <Shop
        open={shop.open}
        hint={shop.hint}
        deviceId={identity.deviceId}
        credits={credits}
        onClose={() => setShop({ open: false, hint: "" })}
        showToast={showToast}
      />
      {toast && (
        <div
          data-testid="toast"
          className={`fixed top-4 left-1/2 -translate-x-1/2 z-[100] px-4 py-3 rounded-xl border text-sm font-medium max-w-[90vw] ${
            toast.type === "error" ? "bg-[#2a1215] border-red-500/40 text-red-300" : "bg-elevated border-white/10 text-white"
          }`}
        >
          {toast.msg}
        </div>
      )}
    </div>
  );
}
